import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  APP_INITIALIZER,
  isDevMode,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { provideRouter, withInMemoryScrolling, withComponentInputBinding, RouteReuseStrategy } from '@angular/router';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  withInterceptors,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { NgZoneInterceptor } from './core/interceptors/ng-zone.interceptor';
import { supabaseTokenInterceptor } from './core/interceptors/supabase-token.interceptor';
import {
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
  MsalInterceptor,
} from '@azure/msal-angular';
import {
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  IPublicClientApplication,
} from '@azure/msal-browser';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { AppRouteReuseStrategy } from './core/navigation/app-route-reuse.strategy';
import { TenantConfigService } from './core/tenant/tenant-config.service';
import { SystemBrandingService } from './core/branding/system-branding.service';
import { ThemeService } from './core/theme/theme.service';
import { ImpersonationService } from './core/impersonation/impersonation.service';
import { provideServiceWorker } from '@angular/service-worker';

/**
 * MSAL PublicClientApplication-Instanz für den Azure-AD-Login.
 * Wird mit den Werten aus `environment.msalConfig` konfiguriert
 * (Client-ID, Tenant-Authority, Redirect-URI).
 * Der Token-Cache wird im LocalStorage abgelegt, damit der Login
 * über Browser-Tab-Neustarts hinweg erhalten bleibt.
 */
const msalInstance = new PublicClientApplication({
  auth: {
    clientId: environment.msalConfig.clientId,
    authority: environment.msalConfig.authority,
    redirectUri: environment.msalConfig.redirectUri,
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
  },
});

/**
 * Factory-Funktion für den `APP_INITIALIZER`.
 * Stellt sicher, dass MSAL vollständig initialisiert ist, bevor die Angular-App
 * den ersten Route-Guard oder HTTP-Interceptor ausführt. Ohne diese Initialisierung
 * würde MSAL beim ersten Redirect-Callback noch nicht bereit sein.
 *
 * @param instance - Die zu initialisierende MSAL-Instanz.
 * @returns Eine Funktion, die ein Promise zurückgibt und von Angular beim App-Start abgewartet wird.
 */
function initializeMsal(instance: IPublicClientApplication): () => Promise<void> {
  return () => instance.initialize().catch(() => {
    // MSAL-Initialisierung darf offline nicht die ganze App blockieren.
    // Ohne Token kann der User zwar nichts tun, aber er sieht wenigstens
    // die Login-Seite oder den Offline-Hinweis statt einem weissen Bildschirm.
    console.warn('[MSAL] Initialisierung fehlgeschlagen (offline?) – App startet ohne Auth.');
  });
}

/**
 * Zentrale Angular-Anwendungskonfiguration (standalone API).
 *
 * Registriert alle plattformweiten Provider:
 * - Router mit den definierten App-Routen
 * - HttpClient mit DI-basierten Interceptoren (für den MsalInterceptor)
 * - Async Animations für MDBootstrap-Komponenten
 * - MSAL: Instanz, Guard-Konfiguration, Interceptor-Konfiguration und Services
 *
 * Der `MsalInterceptor` hängt automatisch einen gültigen Azure-AD-Bearer-Token
 * an alle Requests an die Backend-API (`environment.apiUrl`). Läuft der Token ab
 * oder fehlt er, leitet MSAL den User per Redirect zur Azure-AD-Anmeldeseite.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
      withComponentInputBinding(),
    ),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([supabaseTokenInterceptor]),
    ),
    provideAnimationsAsync(),

    /**
     * Custom RouteReuseStrategy: Haelt Listen-Seiten (Projekte, Auftraege, etc.)
     * im Speicher, damit Scroll-Position und Filter-State beim Zuruecknavigieren
     * von Detail-Seiten erhalten bleiben.
     */
    { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },

    /** Stellt die MSAL-Instanz im DI-Container bereit. */
    { provide: MSAL_INSTANCE, useValue: msalInstance },

    /**
     * Wartet auf die MSAL-Initialisierung, bevor die App startet.
     * Verhindert Race-Conditions beim Verarbeiten des Login-Redirects.
     */
    {
      provide: APP_INITIALIZER,
      useFactory: () => initializeMsal(msalInstance),
      multi: true,
    },

    /**
     * Laedt das System-Branding (Farben, Logo) beim App-Start.
     * Nutzt /api/system/branding (AllowAnonymous) – kein Token noetig.
     * Die vollstaendige TenantConfig wird erst NACH dem Login im roleGuard geladen,
     * wenn der Tenant aus dem Auth-Token bekannt ist.
     */
    /**
     * Branding-Initializer ist offline-resilient: Wenn der HTTP-Call fehlschlaegt
     * (kein Netz, Server nicht erreichbar), startet die App trotzdem – nur ohne
     * Tenant-spezifisches Branding. Der catch() verhindert, dass ein Netzwerkfehler
     * die gesamte App-Initialisierung blockiert (weisser Bildschirm).
     */
    {
      provide: APP_INITIALIZER,
      useFactory: (brandingService: SystemBrandingService, themeService: ThemeService) => () =>
        firstValueFrom(brandingService.laden()).then(branding => {
          if (branding) {
            themeService.applyTenantBranding({
              primaryColor: branding.primaerFarbe,
              accentColor: branding.akzentFarbe,
              logoSvg: branding.logoSvg ?? '',
              logoSvgDark: branding.logoSvgDark,
              logoText: branding.name,
              faviconColor: branding.faviconFarbe,
              webseite: branding.webseite,
            });
            // Browser-Tab-Titel auf Branding-Name setzen (z.B. "LIGNA Systems")
            if (branding.name && branding.name !== 'BauAgent.ai') {
              document.title = branding.name;
            }
          }
        }).catch(() => {
          // Offline oder Server nicht erreichbar – App startet ohne Branding
          console.warn('[Branding] Konnte Branding nicht laden (offline?) – App startet mit Standard-Theme.');
        }),
      deps: [SystemBrandingService, ThemeService],
      multi: true,
    },

    /**
     * Instanziiert den ImpersonationService einmalig beim App-Start, damit
     * er sich im Constructor auf `AuthService.userGeladen$` abonnieren kann.
     * Ohne diesen Touch wuerde der Service erst beim ersten Zugriff (z.B. via
     * ImpersonationBar) erstellt – dann verpasst er das erste userGeladen$-Event.
     */
    {
      provide: APP_INITIALIZER,
      useFactory: (_impersonationService: ImpersonationService) => () => Promise.resolve(),
      deps: [ImpersonationService],
      multi: true,
    },

    /**
     * Konfiguriert den MsalGuard für Redirect-basierte Interaktion.
     * Die angeforderten Scopes (z. B. API-Zugriffsrechte) kommen aus `environment.msalConfig.scopes`.
     */
    {
      provide: MSAL_GUARD_CONFIG,
      useValue: {
        interactionType: InteractionType.Redirect,
        authRequest: { scopes: environment.msalConfig.scopes },
      },
    },

    /**
     * Konfiguriert den MsalInterceptor: Im Microsoft-Modus werden alle Requests
     * an `environment.apiUrl` mit einem Bearer-Token versehen.
     * Im Email-Modus (Supabase) wird MSAL komplett deaktiviert —
     * der SupabaseTokenInterceptor uebernimmt dann die Token-Verwaltung.
     */
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useValue: {
        interactionType: InteractionType.Redirect,
        protectedResourceMap:
          environment.authModus === 'microsoft'
            ? new Map([
                // AllowAnonymous-Endpunkte explizit ausschliessen (null = kein MSAL-Token)
                [`${environment.apiUrl}/api/registrierung`, null],
                [`${environment.apiUrl}/api/system/branding`, null],
                [`${environment.apiUrl}/api/auth/passwort-vergessen`, null],
                [`${environment.apiUrl}/api/auth/passwort-zuruecksetzen`, null],
                // Alle anderen API-Calls bekommen einen MSAL Bearer-Token
                [environment.apiUrl, environment.msalConfig.scopes],
              ])
            : new Map<string, null>([
                // Email-Modus: MSAL greift nirgends ein, Supabase-Interceptor uebernimmt
                [environment.apiUrl, null],
              ]),
      },
    },

    /**
     * NgZoneInterceptor muss VOR dem MsalInterceptor registriert sein.
     * Interceptoren verarbeiten Responses in umgekehrter Reihenfolge –
     * daher verarbeitet NgZoneInterceptor die Response als letzter und
     * bringt sie zurück in Angulars Zone, bevor Change Detection läuft.
     */
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NgZoneInterceptor,
      multi: true,
    },

    /**
     * Registriert den MsalInterceptor als HTTP-Interceptor.
     * `multi: true` stellt sicher, dass er neben anderen Interceptoren aktiv bleibt.
     */
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },

    /**
     * Supabase-Token-Interceptor: wird als funktionaler Interceptor via
     * withInterceptors() registriert (siehe provideHttpClient oben).
     * Vermeidet zirkulaere DI-Abhaengigkeit:
     * AuthService → HttpClient → SupabaseTokenInterceptor → SupabaseService
     */

    /** MSAL-Services für Auth-Status, Route-Schutz und Event-Broadcasting. */
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:5000',
    }),
  ],
};
